<script setup>
import { ref, onMounted, onBeforeUnmount, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import axios from "@/axios";
import Swal from "sweetalert2";
import AuthLayout from "@/components/layouts/AuthLayout.vue";
import jsQR from "jsqr";

const router = useRouter();
const isLoading = ref(false);
const isScanning = ref(false);
const videoRef = ref(null);
const canvasRef = ref(null);
const errorMessage = ref('');

const cleanup = () => {
 isScanning.value = false;
 
 if (videoRef.value && videoRef.value.srcObject) {
   const stream = videoRef.value.srcObject;
   const tracks = stream.getTracks();
   
   tracks.forEach(track => {
     track.stop(); 
     stream.removeTrack(track);
   });
   
   videoRef.value.srcObject = null;
   videoRef.value.load();
 }
 
 if (canvasRef.value) {
   const ctx = canvasRef.value.getContext('2d');
   ctx?.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height);
 }
};

// Iniciar la cámara
const startCamera = async () => {
 isScanning.value = true;
 errorMessage.value = '';
 
 try {
   const stream = await navigator.mediaDevices.getUserMedia({
     video: { facingMode: "environment" }
   });
   
   if (videoRef.value) {
     videoRef.value.srcObject = stream;
     videoRef.value.setAttribute("playsinline", true);
     
     videoRef.value.onloadedmetadata = () => {
       videoRef.value.play();
       requestAnimationFrame(tick);
     };
   }
 } catch (error) {
   console.error("Error al iniciar la cámara:", error);
   errorMessage.value = `No se pudo acceder a la cámara: ${error.message}`;
   isScanning.value = false;
   
   Swal.fire({
     icon: "error",
     title: "Error de cámara",
     text: "No pudimos acceder a la cámara. Por favor verifica los permisos.",
     confirmButtonText: "Entendido",
   });
 }
};

const tick = () => {
 if (!isScanning.value) return;
 
 if (videoRef.value?.readyState === videoRef.value?.HAVE_ENOUGH_DATA) {
   const canvas = canvasRef.value;
   const video = videoRef.value;
   
   canvas.width = video.videoWidth;
   canvas.height = video.videoHeight;
   
   const ctx = canvas.getContext("2d");
   ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
   
   const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
   
   const code = jsQR(imageData.data, imageData.width, imageData.height, {
     inversionAttempts: "dontInvert",
   });
   
   if (code) {
     isScanning.value = false;
     processQRCode(code.data);
     return;
   }
 }
 
 if (isScanning.value) {
   requestAnimationFrame(tick);
 }
};

const processQRCode = async (decodedString) => {
 if (!decodedString) {
   console.error("No se pudo extraer información del QR");
   isScanning.value = true;
   requestAnimationFrame(tick);
   return;
 }
 
 try {
   isLoading.value = true;
   
   const response = await axios.post("/v1/membership/activate-qr", {
     code: decodedString,
   });

   await Swal.fire({
     icon: "success",
     title: "¡Membresía activada!",
     text: response.data.message,
     timer: 2000,
     showConfirmButton: false,
   });

   cleanup();
   router.push("/membership-success");
   
 } catch (error) {
   console.error("Error al activar membresía:", error);

   if (error.response?.status === 429) {
     await Swal.fire({
       icon: "warning",
       title: "Demasiados intentos",
       text: "Has excedido el número de intentos permitidos. Por favor, intenta más tarde.",
       confirmButtonText: "Entendido",
     });
     return;
   }

   await Swal.fire({
     icon: "error",
     title: "Error",
     text: error.response?.data?.message || "Error al activar la membresía",
     confirmButtonText: "Intentar nuevamente",
   });
   
   isScanning.value = true;
   requestAnimationFrame(tick);
   
 } finally {
   isLoading.value = false;
 }
};

router.beforeEach((to, from, next) => {
 if (from.path === '/activate-membership') {
   cleanup();
 }
 next();
});

onMounted(() => {
 startCamera();
});

onBeforeUnmount(() => {
 cleanup();
});

onUnmounted(() => {
 cleanup();
});
</script>

<template>
 <AuthLayout @before-leave="cleanup">
   <template #title>
     <h1 class="text-3xl font-bold text-white mb-2">ESCANEA EL QR</h1>
     <p class="text-white/90">
       Muestra el código QR a la cámara para activar tu membresía
     </p>
   </template>

   <main class="flex-1 flex flex-col items-center justify-center lg:p-4">
     <div class="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden relative">
       <div class="aspect-square relative overflow-hidden">
         <video
           ref="videoRef"
           class="absolute inset-0 w-full h-full object-cover"
           playsinline
         ></video>
         
         <canvas
           ref="canvasRef"
           class="hidden"
         ></canvas>
         
         <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
           <div class="w-4/5 h-4/5 border-2 border-white/30 border-dashed rounded-lg"></div>
           <div class="absolute top-10 left-10 w-10 h-10 border-t-2 border-l-2 border-white/60 rounded-tl-lg"></div>
           <div class="absolute top-10 right-10 w-10 h-10 border-t-2 border-r-2 border-white/60 rounded-tr-lg"></div>
           <div class="absolute bottom-10 left-10 w-10 h-10 border-b-2 border-l-2 border-white/60 rounded-bl-lg"></div>
           <div class="absolute bottom-10 right-10 w-10 h-10 border-b-2 border-r-2 border-white/60 rounded-br-lg"></div>
         </div>
         
         <div class="scanning-line-full"></div>
         
         <div 
           v-if="errorMessage" 
           class="absolute inset-0 bg-black/70 flex items-center justify-center p-4"
         >
           <div class="text-white text-center">
             <p class="font-bold text-red-500 mb-2">Error</p>
             <p>{{ errorMessage }}</p>
             <button 
               @click="startCamera" 
               class="mt-4 px-4 py-2 bg-[#99b642] text-white rounded-lg"
             >
               Reintentar
             </button>
           </div>
         </div>
       </div>

       <div
         v-if="isLoading"
         class="absolute inset-0 bg-black/50 flex items-center justify-center z-30"
       >
         <div class="text-center text-white">
           <div class="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mb-4"></div>
           <p>Procesando...</p>
         </div>
       </div>

       <div class="p-4 bg-gray-50 flex justify-center items-center">
         <button
           @click="() => {
             cleanup();
             router.push('/dashboard');
           }"
           class="text-gray-600 hover:text-gray-800 transition-colors"
         >
           Cancelar
         </button>
       </div>
     </div>
   </main>

   <template #footer-links>
     <p class="text-white/90 max-w-md mx-auto text-center mt-4">
       Apunta al código QR con la cámara y mantén el dispositivo estable
     </p>
     <p class="text-center text-white mt-6">
        ¿Tienes problemas para activar tu membresía?
        <a
          href="mailto:support@hikesocietycam.com"
          class="font-semibold underline hover:text-[#d4e4a9] transition-colors ml-1"
        >
            Escríbenos a support@hikesocietycam.com
        </a>
      </p>
   </template>
 </AuthLayout>
</template>

<style scoped>
@keyframes scanning-full {
 0% {
   top: 0;
   opacity: 0.6;
 }
 50% {
   top: 100%;
   opacity: 1;
 }
 100% {
   top: 0;
   opacity: 0.6;
 }
}

.scanning-line-full {
 position: absolute;
 left: 0;
 right: 0;
 top: 0;
 height: 3px;
 background: linear-gradient(to right, transparent, #99b642, transparent);
 animation: scanning-full 3s ease-in-out infinite;
 z-index: 10;
 width: 100%;
 box-shadow: 0 0 8px rgba(153, 182, 66, 0.6);
}
</style>